import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users,
  Rocket,
  Briefcase,
  Shield,
  Cog,
  Globe,
  Tags,
  Search,
  Lightbulb,
  Code,
  TestTube,
  UploadCloud,
  Headphones,
  Stethoscope,
  Landmark,
  ShoppingCart,
  Factory,
  Car,
  Smartphone,
  GraduationCap,
  Hotel,
  Zap,
  Package,
  Film,
  Wheat,
  ChevronRight,
  ArrowRight,
  Calendar,
  FileText
} from 'lucide-react';

// ============= NAVBAR =============
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.a href="/" className="text-2xl font-bold tracking-tight">
            <span className="text-white">AI Factory</span>
          </motion.a>

          <div className="hidden md:flex items-center gap-12">
            <a href="/" className="text-sm text-gray-300 hover:text-white transition">Home</a>
            <a href="#services" className="text-sm text-gray-300 hover:text-white transition">Services</a>
            <a href="#process" className="text-sm text-gray-300 hover:text-white transition">Process</a>
            <a href="#industries" className="text-sm text-gray-300 hover:text-white transition">Industries</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Contact Us
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badge */}
        <motion.div
          className="flex justify-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <span className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400">
            Enterprise AI Services
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">Enterprise</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            AI Solutions
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-12 font-light tracking-wide max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Transform Your Business with Cutting-Edge AI Technology
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.a
            href="#services"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Explore Services <ArrowRight size={18} />
          </motion.a>
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            Get a Quote
          </motion.a>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= ICON MAP =============
const iconMap = {
  users: Users,
  rocket: Rocket,
  briefcase: Briefcase,
  shield: Shield,
  settings: Cog,
  globe: Globe,
  tag: Tags,
  fileText: FileText
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = iconMap[service.icon] || Users;

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Card */}
      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        {/* Top Gradient Line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          {/* Icon */}
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <IconComponent className="text-teal-300" size={32} />
          </motion.div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          {/* Description */}
          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          {/* Features */}
          <ul className="space-y-3 mb-6">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* Button with Link */}
          <motion.a
            href={service.link}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="w-full py-3 bg-gradient-to-r from-blue-500/20 via-teal-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full text-teal-400 font-semibold hover:from-blue-500/30 hover:via-teal-500/30 hover:to-emerald-500/30 transition-all flex items-center justify-center gap-2"
          >
            Learn More <ArrowRight size={16} />
          </motion.a>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: 'users',
      title: "AI Engineer Staff Augmentation",
      description: "Get skilled AI engineers on-demand to strengthen your team and accelerate your AI projects. Our vetted professionals integrate seamlessly with your existing workflows.",
      link: "/ai-engineer-augmentation",
      features: [
        "Experienced AI/ML engineers with proven track records",
        "Flexible engagement models (full-time, part-time, project-based)",
        "Quick onboarding process (average 48 hours)",
        "Seamless team integration with your existing processes",
        "Cost-effective scaling without long-term commitments"
      ]
    },
    {
      icon: 'rocket',
      title: "AI Project Execution",
      description: "End-to-end AI project delivery from concept to deployment with guaranteed results. We handle everything so you can focus on your core business.",
      link: "/ai-project-execution",
      features: [
        "Complete project management with dedicated PM",
        "Agile development approach with bi-weekly sprints",
        "Comprehensive quality assurance testing",
        "Production deployment & maintenance support",
        "Real-time performance monitoring & optimization"
      ]
    },
    {
      icon: 'briefcase',
      title: "AI Consulting Services",
      description: "Strategic AI guidance to help you make informed decisions and maximize ROI. Our experts help you navigate the AI landscape with confidence.",
      link: "/ai-consulting-services",
      features: [
        "AI readiness assessment & gap analysis",
        "Custom strategy & implementation roadmap",
        "Technology stack selection & evaluation",
        "High-impact use case identification",
        "Detailed implementation planning & budgeting"
      ]
    },
    {
      icon: 'shield',
      title: "AI Security & Governance",
      description: "Ensure your AI systems are secure, compliant, and ethically implemented. Protect your organization from AI-related risks and regulatory issues.",
      link: "/ai-security-governance",
      features: [
        "Comprehensive security audits & penetration testing",
        "Regulatory compliance management (GDPR, CCPA, SOC2)",
        "Ethical AI frameworks & bias detection",
        "Risk assessment & mitigation strategies",
        "Data privacy protection & encryption"
      ]
    },
    {
      icon: 'settings',
      title: "Custom AI/ML Solutions",
      description: "Tailored AI and machine learning solutions designed specifically for your unique business challenges. No off-the-shelf solutions—everything built for you.",
      link: "/custom-ai-ml-solutions",
      features: [
        "Custom model development & training",
        "Algorithm optimization for your specific data",
        "Seamless integration with existing systems",
        "Scalable architecture for growth",
        "Continuous model improvement & retraining"
      ]
    },
    {
      icon: 'globe',
      title: "Full Stack AI Solutions",
      description: "Complete AI infrastructure from data pipelines to user interfaces and everything in between. A turnkey solution for your AI needs.",
      link: "/fullstack-ai-solutions",
      features: [
        "End-to-end data infrastructure setup",
        "Model training, validation & deployment",
        "RESTful API development & documentation",
        "Modern frontend integration (React, Vue, etc.)",
        "Cloud infrastructure (AWS, GCP, Azure)"
      ]
    },
    {
      icon: 'tag',
      title: "Data Labeling Services",
      description: "High-quality data annotation and labeling to train accurate AI models. Our trained annotators ensure your data is labeled correctly the first time.",
      link: "/data-labelling-services",
      features: [
        "Image, video & 3D point cloud annotation",
        "Text classification & NER labeling",
        "Audio transcription & speaker diarization",
        "Multi-stage quality control processes",
        "Scalable workforce for any project size"
      ]
    },
    {
      icon: 'fileText',
      title: "AI Services Content Brochure",
      description: "Comprehensive overview of all our AI capabilities and service offerings. Everything you need to know about partnering with AI Factory.",
      link: "/ai-services-content-brochure",
      features: [
        "Complete service catalog & descriptions",
        "Detailed process methodology",
        "Industry-specific solutions overview",
        "Pricing guides & engagement models",
        "Case studies & success stories"
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            What We Offer
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our AI Services
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Comprehensive AI solutions designed to transform your business operations and drive innovation across every department
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PROCESS SECTION =============
const ProcessSection = () => {
  const steps = [
    { number: '01', icon: Search, title: 'Discovery', description: 'We understand your business goals, challenges, and AI requirements through detailed discussions.' },
    { number: '02', icon: Lightbulb, title: 'Strategy', description: 'Develop a comprehensive AI strategy and roadmap tailored to your specific needs.' },
    { number: '03', icon: Code, title: 'Development', description: 'Build and train AI models using best practices and cutting-edge technology.' },
    { number: '04', icon: TestTube, title: 'Testing', description: 'Rigorous testing and validation to ensure accuracy, reliability, and performance.' },
    { number: '05', icon: UploadCloud, title: 'Deployment', description: 'Seamless deployment and integration with your existing systems and workflows.' },
    { number: '06', icon: Headphones, title: 'Support', description: 'Ongoing monitoring, maintenance, and optimization to ensure continued success.' }
  ];

  return (
    <section id="process" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            How We Work
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Process
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            A proven methodology that delivers results
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full"
                whileHover={{ x: 10 }}
              >
                {/* Left Border Accent */}
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 via-teal-500 to-emerald-500 rounded-l-2xl" />
                
                <div className="pl-4">
                  <div className="text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= INDUSTRIES SECTION =============
const IndustriesSection = () => {
  const industries = [
    { icon: Stethoscope, name: 'Healthcare' },
    { icon: Landmark, name: 'Finance' },
    { icon: ShoppingCart, name: 'Retail' },
    { icon: Factory, name: 'Manufacturing' },
    { icon: Car, name: 'Automotive' },
    { icon: Smartphone, name: 'Technology' },
    { icon: GraduationCap, name: 'Education' },
    { icon: Hotel, name: 'Hospitality' },
    { icon: Zap, name: 'Energy' },
    { icon: Package, name: 'Logistics' },
    { icon: Film, name: 'Media' },
    { icon: Wheat, name: 'Agriculture' }
  ];

  return (
    <section id="industries" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Our Expertise
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Industries We Serve
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Delivering AI solutions across diverse sectors
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:border-teal-500/30 transition-all duration-300"
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-3 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <industry.icon className="text-teal-300" size={24} />
                </div>
                <h3 className="text-sm font-semibold text-emerald-400">
                  {industry.name}
                </h3>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = () => {
  return (
    <section id="contact" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-4xl mx-auto">
        {/* Background Glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Top Gradient Line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Ready to{' '}
            <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Transform Your Business?
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how our AI solutions can accelerate your growth and innovation.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 justify-center"
            >
              Schedule Consultation <Calendar size={20} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40 flex items-center gap-3 justify-center"
            >
              Request Proposal <FileText size={20} />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap justify-center gap-8 mb-8">
          <a href="#services" className="text-gray-400 hover:text-teal-400 transition text-sm font-medium uppercase tracking-wider">Services</a>
          <a href="#process" className="text-gray-400 hover:text-teal-400 transition text-sm font-medium uppercase tracking-wider">Process</a>
          <a href="#industries" className="text-gray-400 hover:text-teal-400 transition text-sm font-medium uppercase tracking-wider">Industries</a>
          <a href="#contact" className="text-gray-400 hover:text-teal-400 transition text-sm font-medium uppercase tracking-wider">Contact</a>
          <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm font-medium uppercase tracking-wider">Privacy Policy</a>
          <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm font-medium uppercase tracking-wider">Terms of Service</a>
        </div>

        <div className="text-center">
          <p className="text-gray-500 text-sm">© 2024 AI Factory. All rights reserved. Powering the future with AI.</p>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AIServicesContentBrochurePage() {
  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar />
      <Hero />
      <ServicesSection />
      <ProcessSection />
      <IndustriesSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}